/**
 * ChequeRegisterModal — Batch #14.335 : registre des chèques émis par période (n°, bénéficiaire, montant, statut encaissé au
 * rapprochement / en circulation / annulé / perdu / gâché), PDF, et annulation d'un n° vierge gâché.
 */
import { useCallback, useEffect, useState } from "react";
import { FileDown } from "lucide-react";
import { api } from "@/api";
import { toast } from "@/lib/toast";
import { Btn, Modal, Pill, downloadPdf, errMsg, inputCls, money, useGlBase } from "../comptabilite/_shared";
import { DateRangeBar, SortTh, monthRange, useSort } from "@/components/ListTools";

const STATUS = {
  cleared: ["Encaissé", "bg-emerald-50 text-emerald-700 border-emerald-200"],
  outstanding: ["En circulation", "bg-sky-50 text-sky-700 border-sky-200"],
  manual: ["Manuscrit", "bg-slate-100 text-slate-600 border-slate-200"],
  void: ["Annulé", "bg-rose-50 text-rose-700 border-rose-200"],
  lost: ["Perdu / opposition", "bg-rose-50 text-rose-700 border-rose-200"],
  spoiled: ["Vierge gâché", "bg-amber-50 text-amber-800 border-amber-200"],
};
export default function ChequeRegisterModal({ onClose, onChanged }) {
  const base = useGlBase();
  const [range, setRange] = useState(() => monthRange());
  const { sort, onSort, apply } = useSort("number", "asc");
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [blank, setBlank] = useState({ number: "", reason: "" });

  const load = useCallback(async () => {
    try { const r = await api.get(`${base}/cheques/register`, { params: range }); setData(r.data); }
    catch (e) { toast.error(errMsg(e, "Registre indisponible")); }
  }, [base, range]);
  useEffect(() => { load(); }, [load]);

  const spoil = async () => {
    setBusy(true);
    try {
      await api.post(`${base}/cheques/void-blank`, blank);
      toast.success(`N° ${blank.number} marqué « vierge gâché »`);
      setBlank({ number: "", reason: "" }); load(); onChanged?.();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  const t = data?.totals;
  return (
    <Modal title="Registre des chèques" sub="Chèques émis par période : n°, bénéficiaire, montant et statut (encaissé = rapproché au relevé bancaire)." onClose={onClose} wide testId="cheque-register-modal"
      footer={<><Btn tone="slate" onClick={onClose} testId="cheque-register-close">Fermer</Btn><Btn icon={FileDown} busy={busy} onClick={() => downloadPdf(`${base}/cheques/register.pdf?from=${range.from}&to=${range.to}`, `registre_cheques_${range.from}_${range.to}.pdf`, setBusy)} testId="cheque-register-pdf">PDF</Btn></>}>
      <div className="flex items-end gap-2 flex-wrap">
        <DateRangeBar from={range.from} to={range.to} onChange={setRange} testId="cheque-register" />
        {t && (
          <div className="flex gap-3 text-xs text-slate-600 ml-auto flex-wrap" data-testid="cheque-register-totals">
            <span>Émis <b className="font-mono">{money(t.issued)}</b></span>
            <span className="text-emerald-700">Encaissés <b className="font-mono">{money(t.cleared)}</b> ({t.nb_cleared})</span>
            <span className="text-sky-700">En circulation <b className="font-mono">{money(t.outstanding)}</b> ({t.nb_outstanding})</span>
            <span className="text-rose-700">Annulés / perdus / gâchés <b>{t.nb_void}</b></span>
          </div>
        )}
      </div>
      <div className="border rounded-xl overflow-hidden mt-3 max-h-[50vh] overflow-y-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 sticky top-0">
            <tr>
              <SortTh label="N°" k="number" sort={sort} onSort={onSort} className="text-left px-2 py-2 w-16" testId="cheque-sort-number" />
              <SortTh label="Date" k="date" sort={sort} onSort={onSort} className="text-left px-2 py-2 w-24" testId="cheque-sort-date" />
              <SortTh label="Bénéficiaire" k="party_name" sort={sort} onSort={onSort} className="text-left px-2 py-2" testId="cheque-sort-party" />
              <th className="text-left px-2 py-2 hidden md:table-cell">Factures</th>
              <SortTh label="Montant" k="amount" sort={sort} onSort={onSort} className="text-right px-2 py-2 w-28" align="right" testId="cheque-sort-amount" />
              <SortTh label="Statut" k="status" sort={sort} onSort={onSort} className="text-left px-2 py-2 w-36" testId="cheque-sort-status" />
              <th className="text-left px-2 py-2 hidden lg:table-cell">Motif / note</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {apply(data?.items || [], { number: (r) => Number(r.number) || r.number }).map((r) => {
              const [label, cls] = STATUS[r.status] || [r.status, "bg-slate-100 text-slate-600 border-slate-200"];
              return (
                <tr key={`${r.number}-${r.payment_id || r.void_id}`} className={r.kind ? "bg-rose-50/30" : ""} data-testid={`cheque-register-row-${r.number}`}>
                  <td className="px-2 py-1.5 font-mono text-xs font-semibold">{r.number}</td>
                  <td className="px-2 py-1.5 font-mono text-xs">{r.date}</td>
                  <td className="px-2 py-1.5">{r.party_name || <span className="text-slate-400">—</span>}</td>
                  <td className="px-2 py-1.5 text-xs text-slate-500 hidden md:table-cell">{r.allocations.filter((a) => a.doc_number).map((a) => `n° ${a.doc_number}`).join(", ") || (r.kind ? "" : "Acompte")}</td>
                  <td className="px-2 py-1.5 text-right font-mono tabular-nums">{r.amount ? money(r.amount) : "—"}</td>
                  <td className="px-2 py-1.5"><Pill cls={cls} testId={`cheque-register-status-${r.number}`}>{label}</Pill></td>
                  <td className="px-2 py-1.5 text-xs text-slate-500 hidden lg:table-cell">{r.reason || r.memo}</td>
                </tr>
              );
            })}
            {data && !data.items.length && <tr><td colSpan={7} className="px-3 py-6 text-center text-sm text-slate-400" data-testid="cheque-register-empty">Aucun chèque émis sur la période.</td></tr>}
          </tbody>
        </table>
      </div>
      <div className="mt-3 border rounded-xl p-3 bg-slate-50/60" data-testid="cheque-blank-void">
        <div className="text-xs font-semibold text-slate-700">Chèque vierge gâché (déchiré, mal imprimé)</div>
        <p className="text-[11px] text-slate-500 mb-2">Le n° est tracé au registre sans décaissement et la séquence saute ce numéro.</p>
        <div className="flex items-end gap-2 flex-wrap">
          <div className="w-28"><input value={blank.number} onChange={(e) => setBlank({ ...blank, number: e.target.value })} placeholder="N°" className={`${inputCls} font-mono py-1`} data-testid="cheque-blank-number" /></div>
          <div className="flex-1 min-w-[12rem]"><input value={blank.reason} onChange={(e) => setBlank({ ...blank, reason: e.target.value })} placeholder="Motif (5 caractères min.)" className={`${inputCls} py-1`} maxLength={300} data-testid="cheque-blank-reason" /></div>
          <Btn tone="slate" disabled={!blank.number.trim() || blank.reason.trim().length < 5} busy={busy} onClick={spoil} testId="cheque-blank-submit">Marquer gâché</Btn>
        </div>
      </div>
    </Modal>
  );
}
